import { motion, HTMLMotionProps } from 'framer-motion'
import { ArrowRight } from 'lucide-react'

type ButtonVariant = 'primary' | 'outline' | 'ghost'
type ButtonSize = 'default' | 'large'

interface ButtonProps extends Omit<HTMLMotionProps<'button'>, 'children'> {
  variant?: ButtonVariant
  size?: ButtonSize
  children: React.ReactNode
  href?: string
  showArrow?: boolean
  'aria-label'?: string
}

const variants: Record<ButtonVariant, string> = {
  primary: 'btn-primary',
  outline: 'btn-outline',
  ghost: 'btn-ghost',
}

const sizes: Record<ButtonSize, string> = {
  default: 'px-5 py-2.5 text-small',
  large: 'px-7 py-3.5 text-base',
}

export function Button({
  variant = 'primary',
  size = 'default',
  children,
  href,
  showArrow = false,
  className = '',
  'aria-label': ariaLabel,
  ...props
}: ButtonProps) {
  const baseClassName = `
    ${variants[variant]}
    ${sizes[size]}
    focus:outline-none focus:ring-2 focus:ring-accent-primary focus:ring-offset-2 focus:ring-offset-vibe-black
    ${className}
  `

  const content = (
    <>
      {children}
      {showArrow && (
        <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" aria-hidden="true" />
      )}
    </>
  )

  if (href) {
    const isExternal = href.startsWith('http')
    return (
      <motion.a
        href={href}
        className={`group ${baseClassName}`}
        whileTap={{ scale: 0.98 }}
        target={isExternal ? '_blank' : undefined}
        rel={isExternal ? 'noopener noreferrer' : undefined}
        aria-label={ariaLabel || (isExternal ? `${children} (opens in new tab)` : undefined)}
      >
        {content}
      </motion.a>
    )
  }

  return (
    <motion.button
      className={`group ${baseClassName}`}
      whileTap={{ scale: 0.98 }}
      aria-label={ariaLabel}
      {...props}
    >
      {content}
    </motion.button>
  )
}
